import type { ReactNode } from 'react'
import { lazy, Suspense } from 'react'
import { Box, Shimmer } from '@n26/nxd'
import kebabCase from 'lodash.kebabcase'
import { renderH1 } from '@pages/LandingPage/renderH1'
import type { LandingPageContent, LandingPageData } from '@pages/LandingPage'
import { EditModule } from '@components/EditModule'
import { ModuleErrorBoundary } from 'app/components/ModuleErrorBoundary'
import { LazyLoadImageContext } from '@contexts/LazyLoadImageContext'

const TextModule = lazy(() => import('app/modules/TextModule'))
const TextMediaIntroModule = lazy(
  () => import('app/modules/TextMediaIntroModule')
)
const FullSizeMediaModule = lazy(
  () => import('app/modules/FullSizeMediaModule')
)
const FeaturesHighlightModule = lazy(
  () => import('app/modules/FeaturesHighlightModule')
)
const FeatureTeaserModule = lazy(
  () => import('app/modules/FeatureTeaserModule')
)
const TwoByTwoModule = lazy(() => import('app/modules/TwoByTwoModule'))
const FactsBannerModule = lazy(() => import('app/modules/FactsBannerModule'))
const FaqModule = lazy(() => import('app/modules/FaqModule'))
const AdditionalBlogPostsModule = lazy(
  () => import('app/modules/AdditionalBlogPostsModule')
)
const CompareModule = lazy(() => import('app/modules/CompareModule'))
const TextMediaModule = lazy(() => import('app/modules/TextMediaModule'))
const PlansLiteModule = lazy(() => import('app/modules/PlansLiteModule'))
const SpanishRiskIndicatorModule = lazy(
  () => import('app/modules/SpanishRiskIndicatorModule')
)
const TextCardsSliderModule = lazy(
  () => import('app/modules/TextCardsSliderModule')
)
const TextMediaEmbedModule = lazy(
  () => import('app/modules/TextMediaEmbedModule')
)
const AccountOverviewModule = lazy(
  () => import('app/modules/AccountOverviewModule')
)
const ButtonModule = lazy(() => import('app/modules/ButtonModule'))
const LinkListModule = lazy(() => import('app/modules/LinkListModule'))
const ImageModule = lazy(() => import('app/modules/ImageModule'))
const LatestPressReleasesModule = lazy(
  () => import('app/modules/LatestPressReleasesModule')
)
const DataTableModule = lazy(() => import('app/modules/DataTableModule'))
const StickyBannerModule = lazy(() => import('app/modules/StickyBannerModule'))

const TextImageGroupModule = lazy(
  () => import('app/modules/TextImageGroupModule')
)

const BudgetCalculatorModule = lazy(
  () => import('app/modules/BudgetCalculatorModule')
)

const InflationCalculatorModule = lazy(
  () => import('app/modules/InflationCalculatorModule')
)

const MonthlyCalculatorModule = lazy(
  () => import('app/modules/MonthlyCalculatorModule')
)

const FiftyThirtyTwentyCalculatorModule = lazy(
  () => import('app/modules/FiftyThirtyTwentyCalculatorModule')
)

const CreditCalculatorModule = lazy(
  () => import('app/modules/CreditCalculatorModule')
)

const GlossaryModule = lazy(() => import('app/modules/GlossaryModule'))

const PlansMatrixContainerModule = lazy(
  () => import('app/modules/PlansMatrixContainerModule')
)

const ModuleWrapper = ({
  children,
  heading,
  moduleId,
  isSticky = false,
}: {
  children: ReactNode
  heading?: string | null
  moduleId?: string
  isSticky?: boolean
}) => {
  const headingFormatted = heading ? kebabCase(heading as string) : undefined

  return (
    <Suspense fallback={<Shimmer height={500} />}>
      <EditModule moduleId={moduleId} isSticky={isSticky}>
        <Box as='section' id={headingFormatted}>
          {children}
        </Box>
      </EditModule>
    </Suspense>
  )
}

export function ModuleRenderer({
  landingPageData,
  latestPressReleases,
}: LandingPageData) {
  if (
    landingPageData.content === undefined ||
    landingPageData.content === null
  ) {
    return null
  }

  const {
    content = [],
    ctaLink,
    qrCodeCta,
    trackingName,
    productPreference,
  } = landingPageData || {}

  const shouldRenderH1List: boolean[] = renderH1(
    content as LandingPageContent[]
  )

  return (
    <>
      {landingPageData.content.map(function render(module, index) {
        function renderModule() {
          if (module === undefined || module === null) {
            return null
          }

          const moduleId = module.sys.id
          const key = `${moduleId}-${index}`

          switch (true) {
            case module.type === 'Error': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <ModuleErrorBoundary />
                </ModuleWrapper>
              )
            }

            case module.type === 'Text': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.heading}
                >
                  <TextModule
                    shouldRenderH1={shouldRenderH1List[index]}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'TextMediaIntro': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.heading}
                >
                  <TextMediaIntroModule
                    shouldRenderH1={shouldRenderH1List[index]}
                    pageCtaLink={ctaLink}
                    qrCodeCta={qrCodeCta}
                    trackingName={trackingName}
                    productPreference={productPreference}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'FullSizeMedia': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.heading}
                >
                  <FullSizeMediaModule
                    shouldRenderH1={shouldRenderH1List[index]}
                    pageCtaLink={ctaLink}
                    qrCodeCta={qrCodeCta}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'FeaturesHighlight': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <FeaturesHighlightModule
                    shouldRenderH1={shouldRenderH1List[index]}
                    pageCtaLink={ctaLink}
                    qrCodeCta={qrCodeCta}
                    trackingName={trackingName}
                    productPreference={productPreference}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'FeatureTeaser': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.heading}
                >
                  <FeatureTeaserModule
                    shouldRenderH1={shouldRenderH1List[index]}
                    pageCtaLink={ctaLink}
                    qrCodeCta={qrCodeCta}
                    trackingName={trackingName}
                    productPreference={productPreference}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'TwoByTwoContainer': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.heading}
                >
                  <TwoByTwoModule
                    shouldRenderH1={shouldRenderH1List[index]}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'FactsBanner': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <FactsBannerModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'Faq': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.heading}
                >
                  <FaqModule
                    shouldRenderH1={shouldRenderH1List[index]}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'TextMedia': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <TextMediaModule
                    pageCtaLink={ctaLink}
                    shouldRenderH1={shouldRenderH1List[index]}
                    trackingName={trackingName}
                    productPreference={productPreference}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'AdditionalBlogPosts': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.heading}
                >
                  <AdditionalBlogPostsModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'Compare': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.headline}
                >
                  <CompareModule
                    shouldRenderH1={shouldRenderH1List[index]}
                    pageCtaLink={ctaLink}
                    qrCodeCta={qrCodeCta}
                    trackingName={trackingName}
                    productPreference={productPreference}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'PlansLite': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.heading}
                >
                  <PlansLiteModule
                    {...module}
                    pageCtaLink={ctaLink}
                    qrCodeCta={qrCodeCta}
                    trackingName={trackingName}
                    productPreference={productPreference}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'SpanishRiskIndicator': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <SpanishRiskIndicatorModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'TextCardsSlider': {
              return (
                <ModuleWrapper
                  moduleId={moduleId}
                  key={key}
                  heading={module.heading}
                >
                  <TextCardsSliderModule
                    {...module}
                    shouldRenderH1={shouldRenderH1List[index]}
                    pageCtaLink={ctaLink}
                    qrCodeCta={qrCodeCta}
                    trackingName={trackingName}
                    productPreference={productPreference}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'TextmediaEmbed': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <TextMediaEmbedModule
                    {...module}
                    pageCtaLink={ctaLink}
                    qrCodeCta={qrCodeCta}
                    trackingName={trackingName}
                    productPreference={productPreference}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'AccountOverview': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <AccountOverviewModule
                    {...module}
                    pageCtaLink={ctaLink}
                    qrCodeCta={qrCodeCta}
                    trackingName={trackingName}
                    productPreference={productPreference}
                    shouldRenderH1={shouldRenderH1List[index]}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'Button': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <ButtonModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'LinkList': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <LinkListModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'Image': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <ImageModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'LatestPressReleases' &&
              latestPressReleases?.length &&
              latestPressReleases.length > 0: {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <LatestPressReleasesModule
                    heading={module.heading}
                    latestPressReleases={latestPressReleases}
                    trackingName={trackingName}
                    productPreference={productPreference}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'DataTable': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <DataTableModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'StickyBanner': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key} isSticky>
                  <StickyBannerModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'TextImageGroup': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <TextImageGroupModule
                    {...module}
                    shouldRenderH1={shouldRenderH1List[index]}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'BudgetCalculator': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <BudgetCalculatorModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'InflationCalculator': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <InflationCalculatorModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'MonthlyCalculator': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <MonthlyCalculatorModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'CreditCalculator': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <CreditCalculatorModule
                    shouldRenderH1={shouldRenderH1List[index]}
                    {...module}
                  />
                </ModuleWrapper>
              )
            }

            case module.type === 'FiftyThirtyTwentyCalculator': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <FiftyThirtyTwentyCalculatorModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'Glossary': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <GlossaryModule {...module} />
                </ModuleWrapper>
              )
            }

            case module.type === 'PlansMatrixContainer': {
              return (
                <ModuleWrapper moduleId={moduleId} key={key}>
                  <PlansMatrixContainerModule {...module} />
                </ModuleWrapper>
              )
            }

            default:
              return null
          }
        }

        // Components above the fold (roughly 2 components on our page)
        // Should have their images eagerly loaded.
        const lazyLoadImages = index > 1

        return (
          <LazyLoadImageContext.Provider value={lazyLoadImages} key={index}>
            {renderModule()}
          </LazyLoadImageContext.Provider>
        )
      })}
    </>
  )
}
